import { DEFAULT_SLIME_COLORS, MAX_PLAYERS, clampStageIndex, type PlayerInfo, type SpawnAssign } from '../../shared/protocol';
import { STAGES } from '../stages/stages';
import { NetClient, MAX_RECONNECT_ATTEMPTS, reconnectDelayMs, type NetEvents } from '../net/NetClient';
import { getTurnstileToken } from '../net/turnstile';
import { loadColor, saveColor } from '../session';

export interface LobbyResult {
  net: NetClient;
  selfId: string;
  players: PlayerInfo[];
  spawns: SpawnAssign;
  code: string;
  color: string;
  stageIndex: number;
  isHost: boolean;
}

export interface LobbyCallbacks {
  onGameStart(result: LobbyResult): void;
  onLeave(): void;
}

/** Konten dibedakan sejak awal: host = pembuat room, guest = tamu gabung. */
export interface LobbyView {
  title: string;
  hint: string;
  showCopy: boolean;
}

export function lobbyView(isHost: boolean): LobbyView {
  return isHost
    ? { title: 'LOBBY', hint: 'Bagikan kode ini ke temanmu', showCopy: true }
    : { title: 'GABUNG ROOM', hint: 'Kamu masuk sebagai tamu', showCopy: false };
}

/** True bila tamu sendirian di room (kemungkinan salah kode / room
 *  phantom): daftar pemain tak menampilkan siapa pun selain diri sendiri. */
export function guestAloneWarning(
  players: PlayerInfo[],
  selfId: string,
  isHost: boolean,
): boolean {
  return !isHost && selfId !== '' && players.length === 1 && players[0]?.id === selfId;
}

const BTN =
  'display:block;width:100%;margin:6px 0;padding:10px 14px;font-size:15px;font-weight:700;' +
  'color:#0a0a0f;background:#5dffd4;border:none;border-radius:10px;cursor:pointer;';
const BTN_GHOST =
  'display:block;width:100%;margin:6px 0;padding:9px 14px;font-size:14px;' +
  'color:#e8e8ef;background:transparent;border:1px solid rgba(232,232,239,0.35);border-radius:10px;cursor:pointer;';
const BTN_DISABLED = 'opacity:0.45;cursor:default;';

/** Ruang tunggu: kode room, daftar pemain, ganti warna, host siap mulai. */
export class Lobby {
  private root: HTMLDivElement;
  private codeEl: HTMLDivElement;
  private codeRow: HTMLDivElement;
  private copyBtn: HTMLButtonElement | null = null;
  private listEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private startBtn: HTMLButtonElement;
  private swatches: HTMLDivElement;
  private net = new NetClient();
  private players: PlayerInfo[] = [];
  private selfId = '';
  private spawns: SpawnAssign = {};
  private stageIndex = 0;
  private stageName!: HTMLSpanElement;
  private stageBtn!: HTMLButtonElement;
  private stageRow!: HTMLDivElement;
  private chatLog!: HTMLDivElement;
  private chatInput!: HTMLInputElement;
  private popup: HTMLDivElement | null = null;
  private popupPick = 0;
  private color: string;
  private done = false;
  /** Error fatal (mis. room tak ditemukan): jangan ditimpa pesan onClose. */
  private fatalError = false;
  /** Auto-reconnect: percobaan berjalan + timer tunda (0 = tak ada). */
  private reconnectAttempt = 0;
  private reconnectTimer = 0;

  constructor(
    private panel: HTMLElement,
    private serverUrl: string,
    private code: string,
    private name: string,
    private isHost: boolean,
    private cb: LobbyCallbacks,
  ) {
    this.color = loadColor(DEFAULT_SLIME_COLORS[0]);
    const view = lobbyView(isHost);
    this.root = document.createElement('div');
    this.root.style.cssText = 'width:min(420px,92vw);text-align:left;';

    const title = document.createElement('h1');
    title.style.cssText = 'text-align:center;font-size:22px;letter-spacing:2px;margin-bottom:4px;';
    title.textContent = view.title;
    this.root.appendChild(title);

    this.codeRow = document.createElement('div');
    this.codeRow.style.cssText =
      'display:flex;align-items:center;justify-content:center;gap:12px;margin:6px 0 2px;';
    this.codeEl = document.createElement('div');
    this.codeEl.style.cssText =
      'font-size:34px;font-weight:800;letter-spacing:8px;color:#5dffd4;';
    this.codeEl.textContent = code;
    this.codeRow.appendChild(this.codeEl);
    if (view.showCopy) {
      this.copyBtn = document.createElement('button');
      this.copyBtn.textContent = 'SALIN';
      this.copyBtn.style.cssText =
        'padding:7px 14px;font-size:13px;font-weight:700;color:#0a0a0f;' +
        'background:#ffd166;border:none;border-radius:8px;cursor:pointer;';
      this.copyBtn.onclick = () => this.copyCode();
      this.codeRow.appendChild(this.copyBtn);
    }
    this.root.appendChild(this.codeRow);
    const hint = document.createElement('div');
    hint.style.cssText = 'text-align:center;font-size:12px;opacity:0.6;margin-bottom:10px;';
    hint.textContent = view.hint;
    this.root.appendChild(hint);

    this.listEl = document.createElement('div');
    this.listEl.style.cssText = 'margin:8px 0;min-height:88px;';
    this.root.appendChild(this.listEl);

    const colorLabel = document.createElement('div');
    colorLabel.style.cssText = 'font-size:13px;opacity:0.8;margin:6px 0 4px;';
    colorLabel.textContent = 'Warna slimemu:';
    this.root.appendChild(colorLabel);
    this.swatches = document.createElement('div');
    this.swatches.style.cssText = 'display:flex;gap:8px;margin-bottom:8px;';
    this.root.appendChild(this.swatches);
    this.renderSwatches();

    const stageRow = document.createElement('div');
    this.stageRow = stageRow;
    stageRow.style.cssText =
      'display:flex;align-items:center;gap:10px;padding:7px 10px;margin:4px 0;' +
      'background:rgba(255,255,255,0.06);border-radius:8px;font-size:14px;';
    const stageLabel = document.createElement('span');
    stageLabel.style.cssText = 'opacity:0.7;font-size:12px;';
    stageLabel.textContent = 'STAGE';
    stageRow.appendChild(stageLabel);
    this.stageName = document.createElement('span');
    this.stageName.style.cssText = 'flex:1;font-weight:700;';
    stageRow.appendChild(this.stageName);
    this.stageBtn = document.createElement('button');
    this.stageBtn.style.cssText =
      'padding:5px 12px;font-size:12px;font-weight:700;color:#0a0a0f;' +
      'background:#ffd166;border:none;border-radius:6px;cursor:pointer;display:none;';
    this.stageBtn.textContent = 'GANTI';
    this.stageBtn.onclick = () => this.openStagePopup();
    stageRow.appendChild(this.stageBtn);
    this.root.appendChild(stageRow);
    this.renderStage();

    // Chat room (log + input).
    const chatLabel = document.createElement('div');
    chatLabel.style.cssText = 'font-size:12px;opacity:0.8;margin:8px 0 2px;';
    chatLabel.textContent = 'CHAT ROOM:';
    this.root.appendChild(chatLabel);
    this.chatLog = document.createElement('div');
    this.chatLog.style.cssText =
      'height:96px;overflow-y:auto;font-size:12px;line-height:1.6;' +
      'background:rgba(0,0,0,0.35);border:1px solid rgba(255,255,255,0.1);' +
      'border-radius:8px;padding:6px 8px;margin-bottom:6px;word-break:break-word;';
    this.root.appendChild(this.chatLog);
    const chatRow = document.createElement('div');
    chatRow.style.cssText = 'display:flex;gap:6px;';
    this.chatInput = document.createElement('input');
    this.chatInput.maxLength = 160;
    this.chatInput.placeholder = 'Ketik pesan…';
    this.chatInput.style.cssText =
      'flex:1;padding:7px 10px;font-size:13px;color:#e8e8ef;background:rgba(0,0,0,0.35);' +
      'border:1px solid rgba(93,255,212,0.4);border-radius:8px;outline:none;';
    this.chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.sendChat();
    });
    const chatBtn = document.createElement('button');
    chatBtn.textContent = 'KIRIM';
    chatBtn.style.cssText =
      'padding:7px 14px;font-size:12px;font-weight:700;color:#0a0a0f;background:#5dffd4;' +
      'border:none;border-radius:8px;cursor:pointer;';
    chatBtn.onclick = () => this.sendChat();
    chatRow.appendChild(this.chatInput);
    chatRow.appendChild(chatBtn);
    this.root.appendChild(chatRow);

    this.statusEl = document.createElement('div');
    this.statusEl.style.cssText = 'font-size:13px;min-height:20px;opacity:0.85;text-align:center;';
    this.statusEl.textContent = 'Menghubungkan…';
    this.root.appendChild(this.statusEl);

    this.startBtn = document.createElement('button');
    this.startBtn.style.cssText = BTN + 'display:none;';
    this.startBtn.textContent = 'SIAP — MULAI MAIN';
    this.startBtn.onclick = () => {
      this.net.sendReady();
      this.statusEl.textContent = 'Memulai…';
      this.startBtn.style.cssText = BTN + BTN_DISABLED + 'display:block;';
    };
    this.root.appendChild(this.startBtn);

    const leave = document.createElement('button');
    leave.style.cssText = BTN_GHOST;
    leave.textContent = 'Keluar';
    leave.onclick = () => this.leave();
    this.root.appendChild(leave);

    this.panel.appendChild(this.root);
    void this.connect();
  }

  /**
   * Token Turnstile hanya bisa dipakai SEKALI (sudah terpakai saat
   * /api/create). Ambil token BARU untuk koneksi WS — tanpa ini server
   * menolak (403) dan browser menunjukkan 'can't establish a connection'.
   */
  private async connect(): Promise<void> {
    let token = '';
    try {
      const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined ?? '').trim();
      token = await getTurnstileToken(siteKey);
    } catch (err) {
      if (this.done) return;
      this.statusEl.textContent = `Verifikasi gagal: ${err instanceof Error ? err.message : 'unknown'}`;
      return;
    }
    if (this.done) return;
    this.dial(token || undefined);
  }

  /** Bangun handler socket sekali; dipakai koneksi awal + reconnect. */
  private buildEvents(): NetEvents {
    return {
      onOpen: () => {
        this.net.sendHello(this.name, this.color);
      },
      onWelcome: (selfId, started, spawns, players, stage) => {
        // Handshake sukses (termasuk hasil reconnect): reset backoff.
        this.reconnectAttempt = 0;
        this.selfId = selfId;
        this.spawns = spawns;
        this.stageIndex = clampStageIndex(stage);
        // Daftar dari welcome dipakai langsung: tamu yang join setelah
        // start (late join) langsung begin dengan list terisi — tanpa ini
        // host tak terlihat karena begin jalan sebelum onPlayers.
        this.players = players;
        this.renderList();
        this.renderStage();
        if (started) {
          this.begin();
        } else {
          const me = players.find((p) => p.id === selfId);
          this.statusEl.textContent = me?.host
            ? 'Menunggu pemain lain…'
            : 'Menunggu host menekan SIAP…';
        }
      },
      onPlayers: (players) => {
        this.players = players;
        this.renderList();
      },
      onStage: (index) => {
        this.stageIndex = clampStageIndex(index);
        this.renderStage();
        this.refreshPopup();
      },
      onStart: (spawns) => {
        this.spawns = spawns;
        this.begin();
      },
      onChat: (_id, name, text) => this.appendChat(name, text),
      onState: () => undefined,
      onBye: () => undefined,
      onError: (message) => {
        if (this.done) return;
        this.statusEl.textContent = `Error: ${message}`;
        if (message.startsWith('Room tidak ditemukan')) this.fatalError = true;
      },
      onClose: () => {
        if (this.done || this.fatalError) return;
        this.scheduleReconnect();
      },
    };
  }

  private dial(token?: string): void {
    this.net.connect(this.serverUrl, this.code, this.buildEvents(), token);
  }

  /** Putus tiba-tiba (sinyal HP, sleep, dsb): coba lagi otomatis dengan kode sama. */
  private scheduleReconnect(): void {
    if (this.done || this.fatalError) return;
    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      this.statusEl.textContent = 'Koneksi putus. Kembali lalu coba lagi.';
      return;
    }
    const wait = reconnectDelayMs(this.reconnectAttempt) + Math.floor(Math.random() * 500);
    this.statusEl.textContent = `Koneksi putus — menyambung ulang… (${this.reconnectAttempt + 1})`;
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => void this.redial(), wait);
  }

  private async redial(): Promise<void> {
    if (this.done || this.fatalError) return;
    this.reconnectAttempt++;
    let token = '';
    try {
      const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined ?? '').trim();
      token = await getTurnstileToken(siteKey);
    } catch {
      // Token gagal: tetap coba tanpa token (server yang menilai).
    }
    if (this.done || this.fatalError) return;
    this.dial(token || undefined);
  }

  private stopReconnect(): void {
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = 0;
  }

  private renderList(): void {
    this.listEl.replaceChildren();
    for (const p of this.players) {
      const row = document.createElement('div');
      row.style.cssText =
        'display:flex;align-items:center;gap:10px;padding:7px 10px;margin:4px 0;' +
        'background:rgba(255,255,255,0.06);border-radius:8px;font-size:14px;';
      const dot = document.createElement('span');
      dot.style.cssText = `width:16px;height:16px;border-radius:50%;background:${p.color};flex:none;`;
      row.appendChild(dot);
      const nm = document.createElement('span');
      nm.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
      nm.textContent = p.name + (p.id === this.selfId ? ' (kamu)' : '');
      row.appendChild(nm);
      if (p.host) {
        const badge = document.createElement('span');
        badge.style.cssText =
          'font-size:11px;font-weight:700;padding:2px 8px;border-radius:6px;background:#ffd166;color:#0a0a0f;';
        badge.textContent = 'HOST';
        row.appendChild(badge);
      }
      this.listEl.appendChild(row);
    }
    const empty = MAX_PLAYERS - this.players.length;
    if (empty > 0) {
      const more = document.createElement('div');
      more.style.cssText = 'font-size:12px;opacity:0.55;text-align:center;padding:4px;';
      more.textContent = `Menunggu ${empty} pemain lagi (maks ${MAX_PLAYERS})`;
      this.listEl.appendChild(more);
    }
    if (guestAloneWarning(this.players, this.selfId, this.isHost)) {
      const warn = document.createElement('div');
      warn.style.cssText =
        'font-size:12px;text-align:center;padding:6px 8px;margin-top:4px;color:#ffd166;' +
        'border:1px solid rgba(255,209,102,0.5);border-radius:8px;';
      warn.textContent = 'Kamu sendirian di room ini — pastikan kode benar dan host sudah masuk.';
      this.listEl.appendChild(warn);
    }
    const me = this.players.find((p) => p.id === this.selfId);
    if (me?.host && !this.done) {
      this.startBtn.style.display = 'block';
      this.statusEl.textContent = `Kamu host — tekan SIAP kalau semua sudah siap (${this.players.length} pemain).`;
    } else if (!this.done) {
      this.startBtn.style.display = 'none';
      if (this.statusEl.textContent === '' || this.statusEl.textContent.startsWith('Kamu host')) {
        this.statusEl.textContent = 'Menunggu host menekan SIAP…';
      }
    }
    this.renderStage();
  }

  private renderSwatches(): void {
    this.swatches.replaceChildren();
    for (const hex of DEFAULT_SLIME_COLORS) {
      const b = document.createElement('button');
      const active = hex.toLowerCase() === this.color.toLowerCase();
      b.title = hex;
      b.style.cssText =
        `width:34px;height:34px;border-radius:50%;background:${hex};cursor:pointer;` +
        (active ? 'outline:3px solid #fff;outline-offset:2px;' : 'border:2px solid rgba(255,255,255,0.25);');
      b.onclick = () => {
        this.color = hex;
        saveColor(hex);
        this.net.sendColor(hex);
        this.renderSwatches();
      };
      this.swatches.appendChild(b);
    }
    const custom = document.createElement('input');
    custom.type = 'color';
    custom.value = this.color;
    custom.title = 'Warna bebas';
    custom.style.cssText = 'width:34px;height:34px;border:none;border-radius:50%;cursor:pointer;background:none;padding:0;';
    custom.oninput = () => {
      this.color = custom.value;
      saveColor(custom.value);
      this.net.sendColor(custom.value);
      this.renderSwatches();
    };
    this.swatches.appendChild(custom);
  }

  private copyCode(): void {
    if (!this.copyBtn) return;
    const flash = (): void => {
      this.copyBtn!.textContent = '✓ TERSALIN';
      window.setTimeout(() => {
        if (this.copyBtn) this.copyBtn.textContent = 'SALIN';
      }, 1600);
    };
    const fallback = (): void => {
      try {
        const ta = document.createElement('textarea');
        ta.value = this.code;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
        flash();
      } catch {
        return;
      }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(this.code).then(flash).catch(fallback);
    } else {
      fallback();
    }
  }

  private sendChat(): void {
    const text = this.chatInput.value.trim();
    if (!text) return;
    this.net.sendChat(text);
    this.chatInput.value = '';
    this.chatInput.focus();
  }

  private appendChat(name: string, text: string): void {
    const line = document.createElement('div');
    const who = document.createElement('span');
    who.style.cssText = 'font-weight:700;color:#9fffe9;margin-right:6px;';
    who.textContent = name;
    const body = document.createElement('span');
    body.textContent = text;
    body.style.color = '#e8e8ef';
    line.appendChild(who);
    line.appendChild(body);
    this.chatLog.appendChild(line);
    while (this.chatLog.children.length > 40) this.chatLog.removeChild(this.chatLog.firstChild!);
    this.chatLog.scrollTop = this.chatLog.scrollHeight;
  }

  private renderStage(): void {
    const def = STAGES[this.stageIndex];
    this.stageName.textContent = `${this.stageIndex + 1}. ${def.name}`;
    // Hak host murni dari server (bukan fallback daftar kosong): tamu tak
    // melihat baris STAGE sama sekali, host melihat + tombol GANTI.
    const me = this.players.find((p) => p.id === this.selfId);
    const isHost = me?.host ?? false;
    this.stageRow.style.display = isHost ? 'flex' : 'none';
    this.stageBtn.style.display = isHost && !this.done ? 'block' : 'none';
  }

  /** Popup pilih stage (tab GANTI). Confirm hanya untuk host. */
  private openStagePopup(): void {
    this.closePopup();
    const me = this.players.find((p) => p.id === this.selfId);
    const isHost = me?.host ?? false;
    this.popupPick = this.stageIndex;
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:10;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(5,5,10,0.7);';
    const box = document.createElement('div');
    box.style.cssText =
      'width:min(440px,94vw);max-height:84vh;overflow:auto;background:#14161f;' +
      'border:1px solid rgba(93,255,212,0.35);border-radius:12px;padding:16px 18px;color:#e8e8ef;';
    const title = document.createElement('div');
    title.style.cssText = 'font-size:16px;font-weight:800;letter-spacing:2px;margin-bottom:8px;';
    title.textContent = 'PILIH STAGE';
    box.appendChild(title);
    const list = document.createElement('div');
    list.style.cssText = 'display:grid;gap:6px;';
    box.appendChild(list);
    const paint = (): void => {
      list.replaceChildren();
      STAGES.forEach((def, i) => {
        const b = document.createElement('button');
        const active = i === this.popupPick;
        b.style.cssText =
          'text-align:left;padding:9px 12px;border-radius:8px;cursor:pointer;color:#e8e8ef;' +
          (active
            ? 'background:rgba(93,255,212,0.14);border:2px solid #5dffd4;'
            : 'background:rgba(255,255,255,0.05);border:2px solid transparent;');
        b.innerHTML =
          `<div style="font-weight:700;">${i + 1}. ${def.name}</div>` +
          `<div style="font-size:12px;opacity:0.7;">${def.desc}</div>`;
        b.onclick = () => {
          this.popupPick = i;
          paint();
        };
        list.appendChild(b);
      });
    };
    paint();
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:8px;margin-top:12px;';
    if (isHost) {
      const ok = document.createElement('button');
      ok.style.cssText =
        'flex:1;padding:9px;font-weight:700;color:#0a0a0f;background:#5dffd4;border:none;border-radius:8px;cursor:pointer;';
      ok.textContent = 'KONFIRMASI';
      ok.onclick = () => {
        this.net.sendStage(this.popupPick);
        this.closePopup();
      };
      row.appendChild(ok);
    }
    const cancel = document.createElement('button');
    cancel.style.cssText =
      'flex:1;padding:9px;color:#e8e8ef;background:transparent;border:1px solid rgba(232,232,239,0.35);border-radius:8px;cursor:pointer;';
    cancel.textContent = isHost ? 'BATAL' : 'TUTUP';
    cancel.onclick = () => this.closePopup();
    row.appendChild(cancel);
    box.appendChild(row);
    overlay.appendChild(box);
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) this.closePopup();
    });
    document.body.appendChild(overlay);
    this.popup = overlay;
  }

  private refreshPopup(): void {
    if (!this.popup) return;
    this.popupPick = this.stageIndex;
    const btns = this.popup.querySelectorAll('button');
    btns.forEach((b, i) => {
      if (i >= STAGES.length) return;
      const active = i === this.popupPick;
      b.style.background = active ? 'rgba(93,255,212,0.14)' : 'rgba(255,255,255,0.05)';
      b.style.border = active ? '2px solid #5dffd4' : '2px solid transparent';
    });
  }

  private closePopup(): void {
    this.popup?.remove();
    this.popup = null;
  }

  private begin(): void {
    if (this.done) return;
    this.done = true;
    this.stopReconnect();
    const me = this.players.find((p) => p.id === this.selfId);
    // Catatan: koneksi net TETAP terbuka (dipakai game); jangan close di sini.
    this.cb.onGameStart({
      net: this.net,
      selfId: this.selfId,
      players: this.players,
      spawns: this.spawns,
      code: this.code,
      color: this.color,
      stageIndex: this.stageIndex,
      isHost: me?.host ?? false,
    });
  }

  private leave(): void {
    if (this.done) return;
    this.done = true;
    this.stopReconnect();
    this.closePopup();
    this.net.close();
    this.cb.onLeave();
  }

  /** Lepas DOM tanpa memutus koneksi (dipakai saat masuk game). */
  release(): void {
    this.done = true;
    this.stopReconnect();
    this.closePopup();
    this.root.remove();
  }

  dispose(): void {
    this.done = true;
    this.stopReconnect();
    this.closePopup();
    this.net.close();
    this.root.remove();
  }
}
